import React, { useEffect } from 'react';

import type { EventStatPeriodItem, EventStatPeriodItemOperator, EventStatFilter } from '@vlabs/api/artemy/model';
import { CrossIcon, PlusIcon } from '@vlabs/icons';
import { getFilterColumnOptions, getOperatorOptions, getFilterValueConfig } from '@vlabs/monokit/entities/charts/chart-form/forms/model/filterMappers';
import { useChartFormTranslation } from '@vlabs/monokit/entities/charts/chart-form/i18n';
import { ControllableValueSelect } from '@vlabs/monokit/shared/ui/value-select';
import { START_OF_DAY, END_OF_DAY } from '@vlabs/shared/config';
import validate from '@vlabs/shared/validators/index.js';
import { Control, Margin, SettingsItemWrapper } from '@vlabs/uikit';
import cn from 'classnames';
import { useFormContext, useFieldArray, Controller } from 'react-hook-form';

import st from './FiltersForm.module.sass';
import { FilterValueControl } from './FilterValueControl';

export function FiltersForm(): React.JSX.Element {
  const { t } = useChartFormTranslation();
  const {
    control,
    setValue,
    clearErrors,
    watch,
    formState: { errors },
  } = useFormContext();

  const { fields, append, remove } = useFieldArray({ control, name: 'getEventStats.filters' });

  useEffect(() => {
    const sub = watch((formData, { name, type }) => {
      if (name?.startsWith('getEventStats.filters')) {
        const [, , index, field] = name.split('.');
        if (field === 'column' && type === 'change' && index) {
          clearErrors(`getEventStats.filters.${index}`);
          const availableFunctions = getOperatorOptions(formData.getEventStats.filters[index]?.column);
          const operator = availableFunctions.length === 1 ? availableFunctions[0] : null;

          setValue(`getEventStats.filters.${index}.operator`, operator);
          setValue(`getEventStats.filters.${index}.value`, null);
        }
        if (field === 'operator' && type === 'change') {
          clearErrors(`getEventStats.filters.${index}`);
          setValue(`.getEventStats.filters.${index}.value`, undefined);
        }
      }
    });

    return () => sub.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch]);

  const filters: EventStatFilter[] = watch('getEventStats.filters', []);

  const onChangePeriod = (
    operator: EventStatPeriodItemOperator,
    inputValue: string,
    formValue: EventStatPeriodItem[],
  ) => {
    let period = [...formValue];
    const hasPeriod = formValue.some((v) => v.operator === operator);

    if (!inputValue || (Array.isArray(inputValue) && inputValue.length === 0)) {
      period = period.filter((p) => p.operator !== operator);
      setValue('getEventStats.period', period);
      return;
    }

    if (hasPeriod) {
      period = period.map((p) => {
        if (p.operator !== operator || !inputValue[0]) return p;
        return { ...p, value: inputValue[0] };
      });
    }

    if (!hasPeriod && inputValue[0]) {
      period.push({ operator, value: inputValue[0] });
    }

    setValue('getEventStats.period', period);
  };

  return (
    <React.Fragment>
      <div className={st.FormTitle}>{t('form.filters.period.label')}</div>
      <div className={st.ColorPanel}>
        <SettingsItemWrapper
          contentClassName={st.SettingsItem__Content}
          title={t('form.filters.period.from')}
          titleClassName={st.SettingsItem__Title}
        >
          <Controller
            control={control}
            name="getEventStats.period"
            render={({ field: { value: periodValue } }) => {
              return (
                <Control.DateInput
                  enableTime
                  id="getEventStats.period"
                  name="getEventStats.period"
                  onChange={(inputValue) => onChangePeriod('gt', inputValue, periodValue)}
                  options={START_OF_DAY}
                  value={periodValue}
                />
              );
            }}
          />
        </SettingsItemWrapper>

        <SettingsItemWrapper
          contentClassName={st.SettingsItem__Content}
          title={t('form.filters.period.to')}
        >
          <Controller
            control={control}
            name="getEventStats.period"
            render={({ field: { value: periodValue } }) => {
              return (
                <Control.DateInput
                  enableTime
                  id="getEventStats.period"
                  name="getEventStats.period"
                  onChange={(inputValue) => onChangePeriod('lt', inputValue, periodValue)}
                  options={END_OF_DAY}
                  value={periodValue}
                />
              );
            }}
          />
        </SettingsItemWrapper>
      </div>

      <div className={cn(st.LineDivider, st.MarginTop)} />

      <div className={st.FormTitle}>{t('form.filters.data.label')}</div>
      {fields?.map((filter, filterIndex) => {
        const columnValue = filters[filterIndex]?.column;
        const operator = filters[filterIndex]?.operator;
        const value = filters[filterIndex]?.value;

        const filterColumnOptions = getFilterColumnOptions({ filters, index: filterIndex });
        const operatorOptions = getOperatorOptions(columnValue);
        const fieldConfig = getFilterValueConfig(columnValue);

        return (
          <div
            className={st.ColorPanel}
            key={filter.id}
          >
            <div>
              <SettingsItemWrapper
                contentClassName={st.SettingsItem__Content}
                title={t('form.filters.field.label')}
                titleClassName={st.SettingsItem__Title}
              >
                <ControllableValueSelect
                  control={control}
                  errors={errors}
                  id={`getEventStats.filters.${filterIndex}.column`}
                  name={`getEventStats.filters.${filterIndex}.column`}
                  options={filterColumnOptions}
                  rules={{ required: validate.required() }}
                />
              </SettingsItemWrapper>

              <SettingsItemWrapper
                contentClassName={st.SettingsItem__Content}
                data-tooltip-content={!columnValue ? t('form.filters.function.tooltip') as unknown as string : undefined}
                disabled={!columnValue}
                title={t('form.filters.function.label')}
                titleClassName={st.SettingsItem__Title}
              >
                <ControllableValueSelect
                  control={control}
                  disabled={!columnValue}
                  errors={errors}
                  id={`getEventStats.filters.${filterIndex}.operator`}
                  name={`getEventStats.filters.${filterIndex}.operator`}
                  options={operatorOptions}
                  rules={{ required: validate.required() }}
                />
              </SettingsItemWrapper>

              <FilterValueControl
                disabled={!operator}
                fieldConfig={fieldConfig}
                filterIndex={filterIndex}
                key={columnValue}
                value={value}
              />
              <div className={st.LineDivider} />
              <div className={st.DeleteButton}>
                <Margin>
                  <Control.Button
                    data-testid="deleteFieldButton"
                    icon={<CrossIcon />}
                    kind="negative"
                    onClick={() => remove(filterIndex)}
                    size="small"
                    variant="flat"
                  >
                    {t('btn.remove')}
                  </Control.Button>
                </Margin>
              </div>
            </div>
          </div>
        );
      })}
      <Control.Button
        data-testid="addFieldButton"
        icon={<PlusIcon />}
        onClick={() => append({ column: null, operator: null, value: null })}
        size="small"
        variant="flat"
      >
        {t('btn.add_field')}
      </Control.Button>
    </React.Fragment>
  );
}
